import type { SxProps, Theme } from "@mui/material";
import { Autocomplete, TextField } from "@mui/material";
import type { ProductTypeOption } from "../models/ProductSize";
import { useActiveProductTypes } from "../hooks/useProductSizes";

interface ProductTypeSelectorProps {
	value: number | null;
	onChange: (value: number | null) => void;
	label?: string;
	fullWidth?: boolean;
	size?: "small" | "medium";
	required?: boolean;
	error?: boolean;
	helperText?: string;
	/** Adds an "All" option and takes away the clear button: a filter always has a value. */
	includeAll?: boolean;
	disabled?: boolean;
	sx?: SxProps<Theme>;
}

const ALL_OPTION: ProductTypeOption = { Id: 0, Name: "All" };

export const ProductTypeSelector = ({
	value,
	onChange,
	label = "Product Type",
	fullWidth = false,
	size = "small",
	required = false,
	error = false,
	helperText,
	includeAll = false,
	disabled = false,
	sx,
}: ProductTypeSelectorProps) => {
	const { data: productTypes = [], isLoading } = useActiveProductTypes();
	const options = includeAll ? [ALL_OPTION, ...productTypes] : productTypes;
	const selected =
		options.find((type) => type.Id === value) ??
		(includeAll ? ALL_OPTION : null);

	return (
		<Autocomplete
			options={options}
			value={selected}
			size={size}
			fullWidth={fullWidth}
			disabled={disabled || isLoading}
			disableClearable={includeAll}
			autoHighlight
			getOptionLabel={(option) => option.Name}
			isOptionEqualToValue={(option, current) => option.Id === current.Id}
			onChange={(_event, option) =>
				onChange(!option || option.Id === ALL_OPTION.Id ? null : option.Id)
			}
			renderOption={(props, option) => {
				const { key, ...rest } = props as typeof props & { key: string };
				return (
					<li key={option.Id} {...rest}>
						{option.Name}
					</li>
				);
			}}
			sx={{ minWidth: 120, ...sx } as SxProps<Theme>}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					required={required}
					error={error}
					helperText={helperText}
					slotProps={{ inputLabel: { shrink: true } }}
				/>
			)}
		/>
	);
};
